import PropTypes from "prop-types";
import React from "react";
import { Box, Card, Button, List, Divider } from "@mui/material";
import { Droppable, Draggable } from "react-beautiful-dnd";
import makeStyles from "@mui/styles/makeStyles";
import ResourceSectionTitle from "./ResourceSectionTitle";
import ResourceListItem from "./ResourceListItem";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4, 5),
    "&:hover > h6  $iconButton": {
      opacity: 1
    }
  },
  title: {
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  listItemContainer: {
    "&:hover $iconButton": {
      opacity: 1
    }
  },
  list: {
    minHeight: theme.spacing(1),
    padding: theme.spacing(2, 0, 2, 0)
  },
  addButton: {
    marginLeft: theme.spacing(2)
  },
  iconButton: {
    opacity: 0,
    transition: "opacity 300ms"
  },
  dragButton: {
    cursor: "move"
  },
  dragButtonIcon: {
    fontSize: "1.125rem",
    color: theme.palette.action.active,
    minWidth: 48
  }
}));

const Resource = ({
  section,
  onAddItems,
  onItemClick,
  onEditSection,
  onDeleteSection,
  onEditItem,
  onDeleteItem,
  dragHandleProps
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const listItem = (item, dragHandleProps) => {
    return (
      <ResourceListItem
        item={item}
        onEditItem={value => onEditItem(item.id, value, section.id)}
        onDeleteItem={() => onDeleteItem(section.id, item.id)}
        onClick={() => onItemClick(section.id, item.id)}
        dragHandleProps={dragHandleProps}
      />
    );
  };
  const draggableListItem = (item, index) => {
    return (
      <Draggable key={item.id} draggableId={`${item.id}`} index={index}>
        {(provided, snapshot) => (
          <div key={index} ref={provided.innerRef} {...provided.draggableProps}>
            {listItem(item, provided.dragHandleProps)}
            {index !== section.items.length - 1 && <Divider />}
          </div>
        )}
      </Draggable>
    );
  };
  const sectionContent = () => {
    return (
      <Droppable droppableId={`${section.id}`}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef}>
            <List className={classes.list}>
              {section.items &&
                section.items.map((item, index) =>
                  draggableListItem(item, index)
                )}
              {provided.placeholder}
            </List>
          </div>
        )}
      </Droppable>
    );
  };

  return (
    <Card className={classes.card}>
      <ResourceSectionTitle
        sectionName={section.name}
        isEmpty={!section.items || section.items.length === 0}
        onEditSection={value => onEditSection(section.id, value)}
        onDeleteSection={() => onDeleteSection(section.id)}
        classes={classes}
        dragHandleProps={dragHandleProps}
      />
      {sectionContent()}
      <Box mt={2}>
        <Button
          className={classes.addButton}
          color="primary"
          variant="contained"
          onClick={() => onAddItems(section.name)}
        >
          {t("learningCenterResources.addItemBtn")}
        </Button>
      </Box>
    </Card>
  );
};

export default Resource;

Resource.propTypes = {
  onDeleteItem: PropTypes.func,
  onDeleteSection: PropTypes.func,
  onEditItem: PropTypes.func,
  onEditSection: PropTypes.func,
  onItemClick: PropTypes.func,
  onAddItems: PropTypes.func,
  section: PropTypes.shape({
    /**
     * The name of the resource section
     */
    name: PropTypes.string,
    /**
     * The resources within the resource section
     */
    items: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * The name of the resource
         */
        name: PropTypes.string,

        /**
         * The type of the resource
         */
        type: PropTypes.string
      })
    )
  }),
  dragHandleProps: PropTypes.any
};
